/**
 * The project's label vocabulary, as filter chips (`KLQ-20260821-001` §B).
 *
 * **One fetch per project, shared.** The registry is reference data — tens of rows, read by
 * the filter bar, by every row that carries a label and by the create form that is coming —
 * so it is fetched once with a five-minute `staleTime` and handed round, never looked up per
 * row (`~/.claude/CLAUDE.md`, data fetching). The version poll's sweep deliberately misses it:
 * `max(updated_at)` over the `task` table says nothing about a project's vocabulary, so a task
 * moving must not throw away a registry that is kept on purpose (`queries.ts`, rule 5).
 *
 * **Any-of, because that is what two chips mean.** The server reads `label_ids` as an OR, and
 * a person clicking *fasáda* and *atika* is asking for the tasks carrying either. All-of would
 * need a second control nobody asked for.
 *
 * **The collision is the server's sentence, not a check here.** Inline creation posts the word
 * and renders the `409` — which names the label the project already has, case-insensitively —
 * rather than scanning the fetched registry first. A client-side scan agrees with the server
 * only until somebody on another screen adds the same word, and disagrees about case in
 * whichever direction it guessed. Colour is not offered: recolouring, renaming and archiving
 * are the project settings surface's (`KLQ-20260820-009`), and half an administration screen
 * in a filter bar is a half that has to keep agreeing with the other half.
 */

import { useState } from 'react';

import { Button, InlineError } from '@/components';
import { useMessages } from '@/lib/i18n';

import { detailOf, taskErrorKey } from './problems';
import { useCreateLabel, useProjectLabels } from './queries';
import styles from './Tasks.module.css';

export interface LabelPickerProps {
  readonly projectId: string;
  readonly labels: readonly string[];
  readonly onChange: (labels: readonly string[]) => void;
}

export function LabelPicker({ projectId, labels, onChange }: LabelPickerProps) {
  const t = useMessages('tasks');
  const registry = useProjectLabels(projectId);
  const create = useCreateLabel(projectId);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  const items = registry.data?.items ?? [];
  const selected = new Set(labels);

  const toggle = (id: string) => {
    onChange(selected.has(id) ? labels.filter((held) => held !== id) : [...labels, id]);
  };

  return (
    <div className={styles.labels}>
      <span className={styles.fieldLabel}>{t('label.legend')}</span>

      {registry.isError ? (
        <InlineError>{detailOf(registry.error) ?? t(taskErrorKey(registry.error))}</InlineError>
      ) : registry.isPending ? (
        <span className={styles.muted}>{t('label.loading')}</span>
      ) : (
        <div className={styles.chips}>
          {items.map((label) => (
            <button
              key={label.id}
              type="button"
              aria-pressed={selected.has(label.id)}
              className={[styles.chip, selected.has(label.id) ? styles.chipOn : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => {
                toggle(label.id);
              }}
            >
              {/* The colour is a token name off the wire, read as a custom property rather
                  than as a hex: `design/project/styles/tokens.css` is canonical and a value
                  copied here would be a second palette (CLAUDE.md, and `scripts/guard.mjs`
                  enforces it). */}
              <span
                className={styles.chipDot}
                style={{ background: `var(--${label.color})` }}
                aria-hidden
              />
              {label.name}
            </button>
          ))}
          {items.length === 0 ? <span className={styles.muted}>{t('label.none')}</span> : null}
        </div>
      )}

      {adding ? (
        <form
          className={styles.labelNew}
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() === '') return;
            create.mutate(name.trim(), {
              onSuccess: (label) => {
                setName('');
                setAdding(false);
                // Created from the filter bar, so it is a filter the moment it exists.
                onChange([...labels, label.id]);
              },
            });
          }}
        >
          <input
            className={styles.textInput}
            value={name}
            maxLength={40}
            aria-label={t('label.newName')}
            placeholder={t('label.newName')}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <Button size="sm" type="submit" variant="primary" loading={create.isPending}>
            {t('label.create')}
          </Button>
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => {
              setAdding(false);
              setName('');
              create.reset();
            }}
          >
            {t('label.cancel')}
          </Button>
        </form>
      ) : (
        <Button
          size="sm"
          variant="tertiary"
          onClick={() => {
            setAdding(true);
          }}
        >
          {t('label.add')}
        </Button>
      )}

      {/* The server's own sentence when it has one: the `409` names the existing label, and a
          generic "that is no longer possible" would throw away the only part a person can act
          on. `problems.ts` records why the fallback is deliberately vague. */}
      {create.isError ? (
        <InlineError>{detailOf(create.error) ?? t(taskErrorKey(create.error))}</InlineError>
      ) : null}
    </div>
  );
}
